import { format } from 'date-fns';
import { client } from '../../../services/apiClient';
import type {
  AdsAdvertiserItem,
  AdsChartResponse,
  AdsCreativeItem,
  AdsCreativePrompt,
  AdsPeriod,
  AdsPromptAd,
  AdsPromptItem,
  AdsSummaryResponse,
} from '@brand24-original/llm-types';

// 'paid-results' / 'brands' / 'items' / 'itemKey' instead of ad vocabulary: ad-blocker filter
// lists (EasyList/AdGuard) block XHR whose URL contains 'ads', 'advertisers', … — query string included.
const ADS_ENDPOINT = '/monitor/paid-results';

/** Optional filters shared by the whole paid-results family — serialized as query params. */
export interface AdsFilters {
  models?: string[];
  promptIds?: number[];
  /** Normalized advertiser domains; empty/absent = all advertisers. */
  advertisers?: string[];
}

const buildQuery = (filters: AdsFilters, extra?: Record<string, string>): string => {
  const params = new URLSearchParams();
  if (filters.models && filters.models.length > 0) {
    params.set('models', filters.models.join(','));
  }
  // An empty list is sent as `promptIds=` on purpose: a prompt group with no prompts must show
  // nothing, not the whole project (the backend treats a present-but-empty list as "match none").
  if (filters.promptIds !== undefined) {
    params.set('promptIds', filters.promptIds.join(','));
  }
  if (filters.advertisers && filters.advertisers.length > 0) {
    params.set('brands', filters.advertisers.join(','));
  }
  for (const [key, value] of Object.entries(extra ?? {})) {
    params.set(key, value);
  }
  const query = params.toString();
  return query ? `?${query}` : '';
};

const formatDay = (date: Date): string => format(date, 'yyyy-MM-dd');

export const getAdsSummary = async (
  projectId: number,
  from: Date,
  to: Date,
  filters: AdsFilters
): Promise<AdsSummaryResponse> => {
  const url = `${ADS_ENDPOINT}/summary/${projectId}/${formatDay(from)}/${formatDay(to)}${buildQuery(filters)}`;
  const { data } = await client(true, true).get<AdsSummaryResponse>(url);
  return data;
};

export const getAdsChart = async (
  projectId: number,
  period: AdsPeriod,
  from: Date,
  to: Date,
  filters: AdsFilters
): Promise<AdsChartResponse> => {
  const url = `${ADS_ENDPOINT}/chart/${projectId}/${period}/${formatDay(from)}/${formatDay(to)}${buildQuery(filters)}`;
  const { data } = await client(true, true).get<AdsChartResponse>(url);
  return data;
};

export const getAdsAdvertisers = async (
  projectId: number,
  from: Date,
  to: Date,
  filters: AdsFilters
): Promise<AdsAdvertiserItem[]> => {
  const url = `${ADS_ENDPOINT}/brands/${projectId}/${formatDay(from)}/${formatDay(to)}${buildQuery(filters)}`;
  const { data } = await client(true, true).get<AdsAdvertiserItem[]>(url);
  return data;
};

export const getAdsCreatives = async (
  projectId: number,
  from: Date,
  to: Date,
  filters: AdsFilters
): Promise<AdsCreativeItem[]> => {
  const url = `${ADS_ENDPOINT}/items/${projectId}/${formatDay(from)}/${formatDay(to)}${buildQuery(filters)}`;
  const { data } = await client(true, true).get<AdsCreativeItem[]>(url);
  return data;
};

export const getAdsCreativePrompts = async (
  projectId: number,
  from: Date,
  to: Date,
  adKey: string,
  filters: AdsFilters
): Promise<AdsCreativePrompt[]> => {
  const url = `${ADS_ENDPOINT}/item-prompts/${projectId}/${formatDay(from)}/${formatDay(to)}${buildQuery(
    filters,
    { itemKey: adKey }
  )}`;
  const { data } = await client(true, true).get<AdsCreativePrompt[]>(url);
  return data;
};

export const getAdsPromptStats = async (
  projectId: number,
  from: Date,
  to: Date,
  filters: AdsFilters
): Promise<AdsPromptItem[]> => {
  const url = `${ADS_ENDPOINT}/prompts/${projectId}/${formatDay(from)}/${formatDay(to)}${buildQuery(filters)}`;
  const { data } = await client(true, true).get<AdsPromptItem[]>(url);
  return data;
};

export const getAdsPromptDetails = async (
  projectId: number,
  monitorId: number,
  from: Date,
  to: Date,
  filters: AdsFilters
): Promise<AdsPromptAd[]> => {
  const url = `${ADS_ENDPOINT}/prompt-details/${projectId}/${monitorId}/${formatDay(from)}/${formatDay(
    to
  )}${buildQuery(filters)}`;
  const { data } = await client(true, true).get<AdsPromptAd[]>(url);
  return data;
};

export type AdsExportView = 'brands' | 'items' | 'prompts';

export const exportAdsCsv = async (
  projectId: number,
  view: AdsExportView,
  from: Date,
  to: Date,
  filters: AdsFilters
): Promise<Blob> => {
  const url = `${ADS_ENDPOINT}/export/${projectId}/${view}/${formatDay(from)}/${formatDay(to)}${buildQuery(filters)}`;
  const { data } = await client(true, true).get<Blob>(url, { responseType: 'blob' });
  return data;
};
